import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BEAD_SIZE_LIST } from '../domain/beads';
import { LIBRARIES } from '../domain/palettes';
import { createPattern } from '../domain/pattern';
import { STITCH_LIST, STITCH_SPECS } from '../domain/stitches';
import type { BeadSizeId, Pattern, Rect, StitchId } from '../domain/types';
import { ImageCropper, fullCrop } from './ImageCropper';
import { defaultChartImageOptions, exportPng, openPrintView, patternToSvg } from '../io/exporters';
import { downloadText, safeFilename, serializePattern } from '../io/format';
import {
  defaultImportOptions,
  imageToPattern,
  loadImageFromFile,
  proportionalHeight,
  renderImportPreview,
} from '../io/imageImport';
import type { DitherMode, ImageImportOptions } from '../io/imageImport';
import { deleteProject, listProjects, loadProject } from '../io/storage';
import type { ProjectSummary } from '../io/storage';
import { Field, Modal, NumberInput, Row, Segmented, Slider, Toggle, useEditor } from './kit';

// ---------------------------------------------------------------- new pattern

export function NewPatternDialog({ onClose }: { onClose: () => void }) {
  const { store } = useEditor();
  const [name, setName] = useState('Untitled pattern');
  const [stitch, setStitch] = useState<StitchId>('loom');
  const [beadSize, setBeadSize] = useState<BeadSizeId>('delica11');
  const [width, setWidth] = useState(31);
  const [height, setHeight] = useState(40);
  const [libraryId, setLibraryId] = useState('delica11');

  const spec = STITCH_SPECS[stitch];

  const create = () => {
    const lib = LIBRARIES.find((l) => l.id === libraryId);
    const starter = lib ? [lib.colors[0], lib.colors[2] ?? lib.colors[1]].filter(Boolean) : [];
    const pattern = createPattern({
      name: name.trim() || 'Untitled pattern',
      stitch,
      beadSize,
      width,
      height,
      palette: starter,
    });
    if (stitch === 'crochet') pattern.ropeShift = 1;
    store.loadPattern(pattern);
    store.status('New pattern');
    onClose();
  };

  return (
    <Modal
      title="New pattern"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={create}>
            Create
          </button>
        </>
      }
    >
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field label="Stitch" hint={spec.blurb}>
        <select value={stitch} onChange={(e) => setStitch(e.target.value as StitchId)}>
          {STITCH_LIST.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </Field>
      {spec.supportsOddCount && (
        <p className="dim tiny">
          {width % 2 === 1 ? 'Odd' : 'Even'}-count peyote — decided by the {width} beads across.
        </p>
      )}
      <Field label="Bead">
        <select value={beadSize} onChange={(e) => setBeadSize(e.target.value as BeadSizeId)}>
          {BEAD_SIZE_LIST.map((b) => (
            <option key={b.id} value={b.id}>
              {b.label}
            </option>
          ))}
        </select>
      </Field>
      <Row>
        <Field label={spec.isRope ? 'Beads around' : 'Columns'}>
          <NumberInput value={width} onChange={setWidth} min={1} max={400} />
        </Field>
        <Field label={spec.isRope ? 'Rounds' : 'Rows'}>
          <NumberInput value={height} onChange={setHeight} min={1} max={400} />
        </Field>
      </Row>
      <Field label="Start with colours from">
        <select value={libraryId} onChange={(e) => setLibraryId(e.target.value)}>
          {LIBRARIES.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </select>
      </Field>
    </Modal>
  );
}

// -------------------------------------------------------------- image import

type AspectKey = 'free' | 'square' | 'landscape' | 'portrait';

const ASPECTS: Record<AspectKey, number | null> = {
  free: null,
  square: 1,
  landscape: 4 / 3,
  portrait: 3 / 4,
};

export function ImageImportDialog({ file, onClose }: { file: File; onClose: () => void }) {
  const { store } = useEditor();
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [libraryId, setLibraryId] = useState('delica11');
  const [lockAspect, setLockAspect] = useState(true);
  const [result, setResult] = useState<Pattern | null>(null);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const [step, setStep] = useState<'crop' | 'convert'>('crop');
  const [crop, setCrop] = useState<Rect | null>(null);
  const [aspect, setAspect] = useState<AspectKey>('free');

  const library = LIBRARIES.find((l) => l.id === libraryId) ?? LIBRARIES[0];
  const [opts, setOpts] = useState<ImageImportOptions>(() => ({
    ...defaultImportOptions(library.colors),
    name: file.name.replace(/\.[^.]+$/, ''),
  }));

  useEffect(() => {
    loadImageFromFile(file).then((loaded) => {
      setImg(loaded);
      setCrop(fullCrop(loaded));
    }, (e: Error) => setError(e.message));
  }, [file]);

  const set = (patch: Partial<ImageImportOptions>) => setOpts((o) => ({ ...o, ...patch }));

  // Keep the grid height tied to the proportions of the cropped area, not of
  // the whole picture — otherwise cropping would squash the beadwork.
  useEffect(() => {
    if (!img || !lockAspect || !crop) return;
    const h = proportionalHeight(crop.w, crop.h, opts.gridW, opts.stitch, opts.beadSize);
    setOpts((o) => (o.gridH === h ? o : { ...o, gridH: h }));
  }, [img, lockAspect, crop, opts.gridW, opts.stitch, opts.beadSize]);

  const recompute = useCallback(() => {
    if (!img || step !== 'convert') return;
    setBusy(true);
    setError(null);
    // Defer so the busy state paints before the (synchronous) conversion runs.
    setTimeout(() => {
      try {
        const r = imageToPattern(img, {
          ...opts,
          candidates: library.colors,
          crop: crop ?? undefined,
        });
        setResult(r.pattern);
      } catch (e) {
        setError((e as Error).message);
        setResult(null);
      } finally {
        setBusy(false);
      }
    }, 0);
  }, [img, opts, library, crop, step]);

  useEffect(() => {
    const t = setTimeout(recompute, 160);
    return () => clearTimeout(t);
  }, [recompute]);

  useEffect(() => {
    const canvas = previewRef.current;
    if (!canvas || !result) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    renderImportPreview(ctx, result, w, h);
  }, [result]);

  const accept = () => {
    if (!result) return;
    store.loadPattern(result);
    store.status(`Imported ${file.name} — ${result.palette.length - 1} colours`);
    onClose();
  };

  const applyAspect = (key: AspectKey) => {
    setAspect(key);
    if (!img) return;
    const ratio = ASPECTS[key];
    if (ratio) setCrop(fullCrop(img, ratio));
  };

  if (step === 'crop') {
    return (
      <Modal
        title={`Crop “${file.name}”`}
        onClose={onClose}
        wide
        footer={
          <>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn"
              disabled={!img}
              onClick={() => img && setCrop(fullCrop(img, ASPECTS[aspect]))}
            >
              Reset crop
            </button>
            <button className="btn primary" disabled={!img || !crop} onClick={() => setStep('convert')}>
              Next: colours
            </button>
          </>
        }
      >
        {error && <div className="banner error">{error}</div>}
        <p className="dim tiny">
          Drag the box to choose the part of the picture that becomes beadwork. Only what is inside it
          is converted.
        </p>
        {img && crop ? (
          <>
            <ImageCropper img={img} crop={crop} onChange={setCrop} aspect={ASPECTS[aspect]} />
            <Field label="Lock shape">
              <Segmented<AspectKey>
                value={aspect}
                onChange={applyAspect}
                options={[
                  { value: 'free', label: 'Free' },
                  { value: 'square', label: '1:1' },
                  { value: 'landscape', label: '4:3' },
                  { value: 'portrait', label: '3:4' },
                ]}
              />
            </Field>
            <p className="dim tiny center">
              Crop: {Math.round(crop.w)} × {Math.round(crop.h)} px of {img.naturalWidth} ×{' '}
              {img.naturalHeight}
            </p>
          </>
        ) : (
          <p className="dim">Loading picture…</p>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title={`Import “${file.name}”`}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={() => setStep('crop')}>
            ‹ Crop
          </button>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={!result || busy} onClick={accept}>
            Use this pattern
          </button>
        </>
      }
    >
      {error && <div className="banner error">{error}</div>}
      <div className="import-grid">
        <div className="import-preview">
          <canvas ref={previewRef} />
          {busy && <div className="busy">Converting…</div>}
          {result && (
            <p className="dim tiny center">
              {result.width} × {result.height} beads · {result.palette.length - 1} colours
            </p>
          )}
        </div>
        <div className="import-controls">
          <Field label="Bead library">
            <select value={libraryId} onChange={(e) => setLibraryId(e.target.value)}>
              {LIBRARIES.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label} ({l.colors.length})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Stitch">
            <select value={opts.stitch} onChange={(e) => set({ stitch: e.target.value as StitchId })}>
              {STITCH_LIST.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Bead size">
            <select value={opts.beadSize} onChange={(e) => set({ beadSize: e.target.value as BeadSizeId })}>
              {BEAD_SIZE_LIST.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label}
                </option>
              ))}
            </select>
          </Field>
          <Row>
            <Field label="Columns">
              <NumberInput value={opts.gridW} onChange={(v) => set({ gridW: v })} min={2} max={400} />
            </Field>
            <Field label="Rows">
              <NumberInput
                value={opts.gridH}
                onChange={(v) => set({ gridH: v })}
                min={2}
                max={400}
              />
            </Field>
          </Row>
          <Toggle checked={lockAspect} onChange={setLockAspect} label="Keep picture proportions" />
          <Field label="Maximum colours" hint="0 uses as many as the library needs.">
            <NumberInput value={opts.maxColors} onChange={(v) => set({ maxColors: v })} min={0} max={80} />
          </Field>
          <Field label="Dithering" hint="Mixes nearby beads to fake shades the palette lacks.">
            <Segmented<DitherMode>
              value={opts.dither}
              onChange={(v) => set({ dither: v })}
              options={[
                { value: 'none', label: 'None' },
                { value: 'floyd', label: 'Floyd' },
                { value: 'atkinson', label: 'Atkinson' },
                { value: 'ordered', label: 'Ordered' },
              ]}
            />
          </Field>
          <Field label="Brightness">
            <Slider min={-80} max={80} value={opts.brightness} onChange={(v) => set({ brightness: v })} />
          </Field>
          <Field label="Contrast">
            <Slider min={-80} max={80} value={opts.contrast} onChange={(v) => set({ contrast: v })} />
          </Field>
          <Field label="Saturation">
            <Slider min={-100} max={100} value={opts.saturation} onChange={(v) => set({ saturation: v })} />
          </Field>
          <Field label="Transparency cut-off" hint="Pixels more see-through than this become empty cells.">
            <Slider min={0} max={255} value={opts.alphaThreshold} onChange={(v) => set({ alphaThreshold: v })} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

// -------------------------------------------------------------------- export

export function ExportDialog({ onClose }: { onClose: () => void }) {
  const { store, state } = useEditor();
  const p = state.pattern;
  const [opts, setOpts] = useState(defaultChartImageOptions());
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<typeof opts>) => setOpts((o) => ({ ...o, ...patch }));

  const guard = async (fn: () => void | Promise<void>) => {
    try {
      setError(null);
      await fn();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Modal title="Export" onClose={onClose} footer={<button className="btn" onClick={onClose}>Done</button>}>
      {error && <div className="banner error">{error}</div>}
      <div className="mini-label">Chart image</div>
      <Field label="Bead width in pixels">
        <Slider min={4} max={48} value={opts.cellPx} onChange={(v) => set({ cellPx: v })} suffix=" px" />
      </Field>
      <Toggle checked={opts.showGrid} onChange={(v) => set({ showGrid: v })} label="Outline every bead" />
      <Toggle checked={opts.showSymbols} onChange={(v) => set({ showSymbols: v })} label="Symbols in each bead" />
      <Toggle checked={opts.showNumbers} onChange={(v) => set({ showNumbers: v })} label="Row and column numbers" />
      <Toggle checked={opts.realistic} onChange={(v) => set({ realistic: v })} label="Shaded, realistic beads" />
      <Toggle checked={opts.includeLegend} onChange={(v) => set({ includeLegend: v })} label="Bead list below chart" />
      <Toggle checked={opts.includeTitle} onChange={(v) => set({ includeTitle: v })} label="Title and size" />
      <Toggle checked={opts.transparent} onChange={(v) => set({ transparent: v })} label="Transparent background" />

      <div className="btn-grid" style={{ marginTop: 12 }}>
        <button className="btn primary" onClick={() => guard(() => exportPng(p, opts))}>
          PNG
        </button>
        <button
          className="btn"
          onClick={() =>
            guard(() =>
              downloadText(`${safeFilename(p.name)}-chart.svg`, patternToSvg(p, opts.cellPx), 'image/svg+xml'),
            )
          }
        >
          SVG
        </button>
        <button
          className="btn"
          onClick={() =>
            guard(() => {
              const how = openPrintView(p, opts);
              store.status(
                how === 'printed'
                  ? 'Opened the print view'
                  : 'Pop-ups are blocked, so the printable page was downloaded — open it and print from there.',
              );
            })
          }
        >
          Print / PDF
        </button>
      </div>

      <div className="mini-label" style={{ marginTop: 16 }}>
        Pattern file
      </div>
      <div className="btn-grid">
        <button
          className="btn"
          onClick={() =>
            guard(() => {
              downloadText(`${safeFilename(p.name)}.bead.json`, serializePattern(p));
              store.markSaved();
            })
          }
        >
          Save .bead.json
        </button>
      </div>
      <p className="dim tiny">
        The .bead.json file keeps everything — stitch, palette, notes — and is what to send someone else
        using this app.
      </p>
    </Modal>
  );
}

// ------------------------------------------------------------------ projects

export function ProjectsDialog({ onClose }: { onClose: () => void }) {
  const { store, state } = useEditor();
  const [items, setItems] = useState<ProjectSummary[]>(() => listProjects());
  const [confirming, setConfirming] = useState<string | null>(null);

  const open = (id: string) => {
    const pattern = loadProject(id);
    if (!pattern) {
      store.status('That saved pattern could not be read.');
      return;
    }
    store.loadPattern(pattern);
    store.status(`Opened “${pattern.name}”`);
    onClose();
  };

  return (
    <Modal title="Saved patterns" onClose={onClose} wide footer={<button className="btn" onClick={onClose}>Close</button>}>
      {items.length === 0 && <p className="dim">Nothing saved yet. Patterns autosave to this browser as you work.</p>}
      <div className="project-grid">
        {items.map((it) => (
          <div key={it.id} className={`project-card${it.id === state.pattern.id ? ' current' : ''}`}>
            <button className="project-thumb" onClick={() => open(it.id)} title="Open">
              {it.thumb ? <img src={it.thumb} alt="" /> : <div className="no-thumb" />}
            </button>
            <div className="project-meta">
              <div className="project-name">{it.name}</div>
              <div className="dim tiny">
                {it.width}×{it.height} · {it.stitch} · {new Date(it.modified).toLocaleDateString()}
              </div>
              {confirming === it.id ? (
                <div className="row" style={{ gap: 6 }}>
                  <button
                    className="chip danger"
                    onClick={() => {
                      deleteProject(it.id);
                      setItems(listProjects());
                      setConfirming(null);
                    }}
                  >
                    Delete for good
                  </button>
                  <button className="chip" onClick={() => setConfirming(null)}>
                    Keep
                  </button>
                </div>
              ) : (
                <div className="row" style={{ gap: 6 }}>
                  <button className="chip" onClick={() => open(it.id)}>
                    Open
                  </button>
                  <button className="chip" onClick={() => setConfirming(it.id)}>
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------- shortcuts

const SHORTCUTS: [string, string][] = [
  ['B / E', 'Pencil / eraser'],
  ['L, R, O', 'Line, rectangle, ellipse'],
  ['G', 'Flood fill'],
  ['H', 'Replace one colour everywhere'],
  ['I', 'Pick the colour under the cursor'],
  ['M', 'Select, then drag to move'],
  ['Alt-drag / middle-drag', 'Pan the chart'],
  ['Wheel', 'Zoom at the pointer'],
  ['Shift-wheel', 'Scroll sideways'],
  ['0', 'Fit the pattern to the window'],
  ['+ / −', 'Zoom in and out'],
  ['⌘/Ctrl Z', 'Undo'],
  ['⌘/Ctrl ⇧ Z', 'Redo'],
  ['⌘/Ctrl C / X / V', 'Copy, cut, paste the selection'],
  ['Enter', 'Drop a floating selection'],
  ['Delete', 'Clear the selection'],
  ['Esc', 'Drop the selection or cancel'],
  ['1…9', 'Choose palette colour'],
  ['[ / ]', 'Smaller / bigger brush'],
  ['X', 'Swap the left and right click colours'],
  ['⌘/Ctrl S', 'Save to this browser'],
];

export function ShortcutsDialog({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="Keyboard shortcuts" onClose={onClose} footer={<button className="btn" onClick={onClose}>Close</button>}>
      <table className="shortcuts">
        <tbody>
          {SHORTCUTS.map(([k, v]) => (
            <tr key={k}>
              <td>
                <kbd>{k}</kbd>
              </td>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
}

// ----------------------------------------------------------------- about/help

export function AboutDialog({ onClose }: { onClose: () => void }) {
  const stitches = useMemo(() => STITCH_LIST, []);
  return (
    <Modal title="About Beading Designer" onClose={onClose} footer={<button className="btn" onClick={onClose}>Close</button>}>
      <p>
        A bead pattern editor that runs entirely in your browser. Nothing is uploaded anywhere — patterns
        autosave into this browser's storage, and everything else happens through files you download.
      </p>
      <div className="mini-label" style={{ marginTop: 12 }}>
        Stitches
      </div>
      <ul className="stitch-list">
        {stitches.map((s) => (
          <li key={s.id}>
            <strong>{s.label}</strong>
            <span className="dim"> — {s.blurb}</span>
          </li>
        ))}
      </ul>
      <div className="mini-label" style={{ marginTop: 12 }}>
        A note on colours
      </div>
      <p className="dim small">
        The Delica and Toho libraries are approximate screen colours meant for designing, not for matching
        dye lots. Codes and finishes differ from what a monitor can show. Check a physical colour card
        before you order, and use Palette → Import CSV if you keep your own accurate list.
      </p>
    </Modal>
  );
}
